import {useEffect, useState} from "react";
import {Alert, Box, CircularProgress, Paper, Tab, Tabs, Typography} from "@mui/material";
import {Inventory2Outlined, ReceiptLongOutlined, ShoppingBasketOutlined} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import axiosInstance from "../config/axios-config";
import {GetLogo} from "../profile/main/api/photo-data-handler";
import {PageContainer} from "../components/common/page-container";
import WorkspaceHeader from "../components/common/workspace-header";
import useTenantProfile from "./use-tenant-profile";
import ProfileNavigationGuard from "./profile-navigation-guard";
import TabOverview from "./pharmacy-tabs/tab-overview";
import TabAnalytics from "./pharmacy-tabs/tab-analytics";
import TabTopProducts from "./pharmacy-tabs/tab-top-products";
import TabProfileSettings from "./pharmacy-tabs/tab-profile-settings";

const TABS = ["Overview", "Analytics", "TopProducts", "WorkspaceCompanyProfile"];

export default function PharmacyProfileManagement({navOpen, tenantId, tenantType}) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [pharmacy, setPharmacy] = useState(null);
    const [logoUrl, setLogoUrl] = useState("");
    const [tabValue, setTabValue] = useState(0);
    const [pendingTab, setPendingTab] = useState(null);
    const profile = useTenantProfile(tenantId, pharmacy, setPharmacy);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true); setPharmacy(null); setPendingTab(null); setTabValue(0);
        axiosInstance.get(`/api/v1/tenants/${tenantId}`, {signal: controller.signal})
            .then(({data}) => {if (!controller.signal.aborted) setPharmacy(data);})
            .catch(() => {if (!controller.signal.aborted) setPharmacy(null);})
            .finally(() => {if (!controller.signal.aborted) setLoading(false);});
        return () => controller.abort();
    }, [tenantId]);

    useEffect(() => {
        let active = true;
        setLogoUrl("");
        if (pharmacy?.imageUrl) GetLogo({filename: pharmacy.imageUrl})
            .then(url => {if (active) setLogoUrl(url || "");})
            .catch(() => {if (active) setLogoUrl("");});
        return () => {active = false;};
    }, [tenantId, pharmacy?.imageUrl]);

    const selectTab = value => {
        if (profile.saving || value === tabValue) return;
        if (profile.dirty) setPendingTab(value);
        else setTabValue(value);
    };
    const actions = [{label: t("WorkspaceBrowseProducts"), icon: <Inventory2Outlined/>, onClick: () => navigate("/pharmacy/products")},
        {label: t("WorkspaceViewOrders"), icon: <ReceiptLongOutlined/>, onClick: () => navigate("/pharmacy/my-orders")},
        {label: t("WorkspaceViewBasket"), icon: <ShoppingBasketOutlined/>, onClick: () => navigate("/basket")}];

    return <PageContainer component="main" navOpen={navOpen}>
        <ProfileNavigationGuard dirty={profile.dirty} saving={profile.saving} pendingTab={pendingTab}
            onKeep={() => setPendingTab(null)} onDiscard={() => {
                profile.toggleEdit(); setTabValue(pendingTab); setPendingTab(null);
            }}/>
        {profile.notice && <Alert severity={profile.notice.severity} sx={{mb: 2}}
            role={profile.notice.severity === "success" ? "status" : "alert"}>{profile.notice.message}</Alert>}
        {loading ? <Box py={6} display="flex" justifyContent="center"><CircularProgress aria-label={t("Loading")}/></Box>
            : pharmacy ? <>
                <WorkspaceHeader title={t("WorkspacePharmacyTitle")} companyName={pharmacy.tenantNameEn} logoUrl={logoUrl}
                    typeLabel={t("Pharmacy")} disabled={profile.saving} actions={tenantType === "PHARMACY" ? actions : []}/>
                <Paper variant="outlined" sx={{borderRadius: 2, mb: 2.5, minWidth: 0}}>
                    <Tabs value={tabValue} onChange={(_, value) => selectTab(value)} aria-label={t("WorkspacePharmacyTabs")}
                        variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
                        {TABS.map((label, index) => <Tab key={label} label={t(label)} id={`pharmacy-tab-${index}`}
                            aria-controls={`pharmacy-tabpanel-${index}`}/>)}
                    </Tabs>
                </Paper>
                <Box sx={{minWidth: 0, "& .MuiCard-root": {boxShadow: "none", border: 1, borderColor: "divider", borderRadius: 2}}}>
                    <TabOverview tabValue={tabValue}/>
                    <TabAnalytics tabValue={tabValue} tenantId={tenantId}/>
                    <TabTopProducts tabValue={tabValue}/>
                    <TabProfileSettings tabValue={tabValue} tenantId={tenantId} distributor={pharmacy} tenantType={pharmacy.tenantType}
                        logoUrl={logoUrl} onLogoUpdate={(tenant, url) => {setPharmacy(tenant); setLogoUrl(url);}}
                        onWorkingHoursUpdate={setPharmacy} editMode={profile.editMode} saving={profile.saving} errors={profile.errors}
                        editedDistributor={profile.draft} onEditToggle={profile.toggleEdit} onSave={profile.save}
                        onInputChange={(field, value) => profile.setDraft(previous => ({...previous, [field]: value}))}
                        onAddressChange={(field, value) => profile.setDraft(previous => ({...previous,
                            address: {...previous.address, [field]: value}}))}/>
                </Box>
            </> : <Typography variant="h6" sx={{textAlign: "center", py: 4}}>{t("PharmacyNotFound")}</Typography>}
    </PageContainer>;
}
